import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "qs";
import PropTypes from "prop-types";
import jwt from "jwt-simple";
import TagManager from "react-gtm-module";
import setUserTokens from "./helpers/setUserTokens";
import clearUserTokens from "./helpers/clearUserTokens";
import sessionTimer from "./helpers/sessionTimer";
import redirectToHpa from "./helpers/redirectToHpa";
import axios from "axios";

/*

This is the place to do those early async configuration calls
so they'll be available for the whole app.

The authenticated section is simplified on purpose.
The user token is not safe, it is only an example.
For real apps, a more robust solution like hStream ID should be integrated.

*/

const AppContext = React.createContext();
const { Provider, Consumer } = AppContext;

const AppProvider = props => {
  const { children } = props;
  const history = useHistory();
  const [smallNav, setSmallNav] = useState(false);
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [authInProgress, setAuthInProgress] = useState(false); // Hannah Neal: authInProgress is used in the Site component to determine when to show an overlay because the user has the sign-in modal open (i.e., the user's authorization is in progress).
  const [tokenExpired, setTokenExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [queryParameters, setQueryParameters] = useState(undefined);
  const [token, setToken] = useState(null);
  const [accountsLength, setAccountsLength] = useState(undefined);
  const [timezone, setTimezone] = useState("America/New_York");
  const [config, setConfig] = useState({
    configApiBaseUrl:
      window.envConfig?.dataextract_api_endpoint ||
      process.env?.REACT_APP_DATAEXTRACT_API_DOMAIN,
    gtm: window.envConfig?.react_app_gtm_id || process.env?.REACT_APP_GTM_ID,
    identityPortalUrl:
      window.envConfig?.hstream_api_endpoint ||
      process.env?.REACT_APP_IDENTITY_PORTAL_DOMAIN
  });
  const [StoreAPIDefaults, setStoreAPIDefaults] = useState({
    baseURL: `${config.configApiBaseUrl}`,
    accessToken: null,
    headers: {}
  });

  const { pathname } = useLocation();

  const tagManagerArgs = {
    gtmId: config.gtm
  };

  const redirectUserToHpa = destination => {
    redirectToHpa(
      config.identityPortalUrl,
      config.configApiBaseUrl,
      destination,
      setAuthInProgress,
      clearUserTokens
    );
    setAuthInProgress(true);
  };

  const signIn = () => {
    redirectUserToHpa();
    setAuthInProgress(true);
  };

  const signOut = () => {
    clearUserTokens();
    setLoggedIn(false);
    setUser(undefined);
    history.push("/");
  };

  function kickOut() {
    setLoggedIn(false);
    setLoading(false);
  }

  const getAccounts = accessToken => {
    axios
      .get(`${config.configApiBaseUrl}/accounts`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })
      .then(response => {
        setLoading(false);
        setAccountsLength(response.data?.data?.length);
        if (pathname === "/app" || pathname === "/") {
          if (response.data?.data?.length > 1) {
            history.push("/app");
          } else if (response.data?.data?.length === 0) {
            history.push({
              pathname: "/errordisplay"
            });
          } else {
            const account = {
              id: response.data?.data[0].accountid,
              name: response.data?.data[0].accountname
            };
            setAccountsLength(response.data?.data?.length);

            localStorage.setItem("accountDeatils", JSON.stringify(account));
            history.push(`/app/account/${account.id}`);
          }
        }
      })
      .catch(err => {
        if (err?.response?.status === 401) {
          signOut();
        } else {
          history.push({
            pathname: "/app/errordisplay",
            state: {
              error: err
            }
          });
        }
        setLoading(false);
      });
  };

  const getUserProfile = accessToken => {
    axios
      .get(`${config.configApiBaseUrl}/profile?epoch=${Date.now()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })
      .then(response => {
        setLoading(false);
        const { data } = response.data;
        setUser(data);
      })
      .catch(err => {
        history.push({
          pathname: "/app/errordisplay",
          state: {
            error: err
          }
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    const checkIfUserIsLoggedIn = async () => {
      try {
        let accessToken = localStorage.getItem("Hstm.Identity.AccessToken");
        let idToken = localStorage.getItem("Hstm.Identity.IdentityToken");
        const params = qs.parse(window.location.search, {
          ignoreQueryPrefix: true
        });

        // check to see if new auth params are present
        if (params.access_token && params.id_token) {
          accessToken = params.access_token;
          idToken = params.id_token;
          setQueryParameters(window.location.search);
          if (accessToken) {
            setUserTokens(idToken, accessToken);
          }
          window.location.href = `${window.location.origin}${window.location.pathname}`;
        }

        // wait for initial site load to finish
        if (accessToken !== null && idToken !== null) {
          // HN: this refreshes the main page in the background while preserving state and closing the modal pop-up.
          if (window.name === "hstream") {
            setLoggedIn(true);
            window.stop();
            window.close();
            window.opener.location.reload();
            window.opener.focus();
            setAuthInProgress(true);
          }

          setLoggedIn(true);
          setStoreAPIDefaults({ accessToken: `${accessToken}` });
          getUserProfile(accessToken);
          getAccounts(accessToken);

          const identityClaims = jwt.decode(idToken);
          setToken(identityClaims);

          // HN: check token for the expiration property and compare it to the current time in milliseconds (e.g., current time is 100 seconds and token expires at 130 seconds).
          if (Date.now() >= identityClaims.exp * 1000) {
            setTokenExpired(true);
            setLoggedIn(false);
          }
        } else {
          kickOut();
        }
      } catch (err) {
        if (
          err.message ===
          "NotAuthorizedException: Token is not from a supported provider of this identity pool."
        ) {
          clearUserTokens();
          kickOut();
        }
      }
    };
    checkIfUserIsLoggedIn();

    sessionTimer(setLoggedIn, setTokenExpired, clearUserTokens, tokenExpired);

    setReady(true);
  }, [loggedIn, tokenExpired]);

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
  }, [tagManagerArgs]);

  return (
    <Provider
      value={{
        user,
        signIn,
        signOut,
        loggedIn,
        authInProgress,
        tokenExpired,
        redirectToHpa,
        loading,
        setUser,
        setLoggedIn,
        setAuthInProgress,
        setLoading,
        setTokenExpired,
        queryParameters,
        clearUserTokens,
        token,
        StoreAPIDefaults,
        ready,
        smallNav,
        setSmallNav,
        timezone,
        setTimezone,
        config,
        setConfig,
        accountsLength
      }}
    >
      {children}
    </Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export { AppProvider, Consumer as AppConsumer, AppContext };
